import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, Loader2 } from "lucide-react";
import PricingCalculator from "../../components/site/PricingCalculator";
import { api, errMsg, fmtDate, money, PLANS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ReferralCard } from "./Referral";

const STATE_LABEL = { trial: "Free trial", active: "Active", read_only: "Expired — read-only", locked: "Locked", suspended: "Suspended" };

const Info = ({ label, value, testid }) => <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p><p className="mt-1 font-semibold text-slate-900" data-testid={testid}>{value}</p></div>;

function ActiveSub({ o, used, free, reload }) {
  const [users, setUsers] = useState(o.seats_purchased);
  const [busy, setBusy] = useState("");
  const p = PLANS[o.plan];
  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  const diff = users - o.seats_purchased;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="font-semibold text-lg">Change user licences</h2>
        <p className="mt-1 text-sm text-slate-600">Additional users cost {money(p.seat)} {p.period}. Increases are prorated for the remaining term and charged immediately. Reductions take effect immediately without a refund for the remaining term.</p>
        <div className="mt-5 flex items-center gap-3">
          <input type="number" min={Math.max(1, used - free)} max={100} value={users} onChange={(e) => setUsers(Number(e.target.value) || 1)} className="field w-28 font-mono" data-testid="seats-change-input" />
          <button disabled={!!busy || diff === 0} onClick={() => run("seats", () => api.post("/billing/seats", { users }), "User licences updated")} className="bg-sky-600 disabled:bg-slate-300 text-white font-semibold px-4 py-2.5 rounded-md text-sm" data-testid="seats-change-btn">{busy === "seats" ? "Updating…" : "Update licences"}</button>
        </div>
        {diff > 0 && <p className="mt-3 text-sm text-slate-600">Adding {diff} user(s): {money(diff * p.seat)} {p.period}, prorated today.</p>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h2 className="font-semibold text-lg">Payment & subscription</h2>
        <button disabled={!!busy} onClick={() => run("portal", () => api.post("/billing/portal", { origin_url: window.location.origin }))} className="w-full border border-slate-300 hover:bg-slate-50 font-semibold py-2.5 rounded-md text-sm" data-testid="billing-portal-btn">{busy === "portal" ? "Opening…" : "Manage payment method & invoices"}</button>
        {o.cancel_at_period_end
          ? <button disabled={!!busy} onClick={() => run("resume", () => api.post("/billing/resume"), "Automatic renewal resumed")} className="w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-md text-sm" data-testid="billing-resume-btn">Resume automatic renewal</button>
          : <button disabled={!!busy} onClick={() => window.confirm("Cancel automatic renewal? Access continues until the end of the paid period.") && run("cancel", () => api.post("/billing/cancel"), "Subscription will not renew")} className="w-full text-red-700 border border-red-200 hover:bg-red-50 font-semibold py-2.5 rounded-md text-sm" data-testid="billing-cancel-btn">Cancel subscription</button>}
      </div>
    </div>
  );
}

export default function Billing() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback((force) => api.get("/billing/overview", { params: { refresh: !!force } }).then((r) => { setD(r.data); refresh(); }).catch((e) => toast.error(errMsg(e))), [refresh]);
  useEffect(() => {
    load(params.get("refresh") === "1");
  }, [load, params]);
  if (!d) return <p className="text-slate-500">Loading…</p>;
  const { org: o, access, invoices, seats_used, free_seats } = d;
  const hasSub = o.stripe_subscription_id && ["active", "trialing", "past_due"].includes(o.subscription_status);
  const checkout = async (plan, users) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, users, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="billing-page">
      <div className="flex justify-between items-end gap-4">
        <div><p className="eyebrow">Administration</p><h1 className="mt-2 text-3xl font-bold tracking-tight">Billing &amp; Plan</h1></div>
        <button onClick={() => load(true)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-300 bg-white px-3 py-2 rounded-md" data-testid="billing-refresh-btn"><RefreshCw className="w-4 h-4" />Refresh</button>
      </div>
      {params.get("cancelled") && <p className="text-sm bg-slate-100 border border-slate-200 rounded-md p-3">Checkout was cancelled. No payment was taken.</p>}
      <div className="bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="billing-summary">
        <Info label="Organisation" value={o.name} testid="billing-org-name" />
        <Info label="Current plan" value={o.plan ? `${PLANS[o.plan].label} plan` : "Free trial"} testid="billing-plan" />
        <Info label="Status" value={STATE_LABEL[access.state] || access.state} testid="billing-status" />
        <Info label={access.state === "active" ? (o.cancel_at_period_end ? "Access ends" : "Renews on") : access.state === "trial" ? "Trial ends" : "Ended on"} value={fmtDate(access.ends_at)} testid="billing-date" />
        <Info label="Purchased users" value={o.seats_purchased} testid="billing-seats-purchased" />
        <Info label="Free users (complimentary)" value={free_seats || 0} testid="billing-free-seats" />
        <Info label="Active users" value={seats_used} testid="billing-seats-used" />
        <Info label="Available licences" value={Math.max(0, o.seats_purchased + (free_seats || 0) - seats_used)} testid="billing-seats-available" />
        <Info label="Tenant ID" value={<span className="font-mono text-sm">{o.tenant_id}</span>} />
      </div>
      {(o.free_seat_grants || []).filter((g) => !g.ended && new Date(g.until) > new Date()).map((g) => <p key={g.id} className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3" data-testid="billing-free-seat-grant">{g.seats} free user licence(s) until {fmtDate(g.until)}. After that they are charged at the normal price.</p>)}
      {o.free_months_credit > 0 && <p className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3" data-testid="billing-free-months-credit">You have {o.free_months_credit} free month(s) waiting. They are applied automatically when you subscribe.</p>}
      {access.payment_failed && <p className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-md p-3" data-testid="billing-payment-failed">Your latest payment failed. Use "Manage payment method" to update your card.</p>}
      {hasSub ? <ActiveSub o={o} used={seats_used} free={free_seats || 0} reload={() => load(true)} /> : (
        <div>
          <h2 className="text-xl font-semibold mb-1">{access.state === "trial" ? "Choose your plan" : "Renew your subscription"}</h2>
          <p className="text-sm text-slate-600 mb-5">Secure checkout by Stripe (test mode). Full access is restored automatically once payment is confirmed.</p>
          <PricingCalculator onCheckout={checkout} busy={busy} initialPlan={o.selected_plan || "monthly"} initialUsers={Math.max(o.selected_users || 1, seats_used - (free_seats || 0))} minUsers={Math.max(1, seats_used - (free_seats || 0))} />
        </div>
      )}
      <ReferralCard />
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <div className="px-5 py-4 border-b border-slate-200 font-semibold">Billing history</div>
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-slate-100"><td className="px-4 py-3">{fmtDate(i.created)}</td><td className="px-4 py-3 font-mono text-xs">{i.number || "—"}</td><td className="px-4 py-3">{money(i.status === "paid" ? i.amount_paid : i.amount_due)}</td><td className="px-4 py-3 capitalize">{i.status}</td>
                <td className="px-4 py-3 text-right">{i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-sky-700 font-semibold">View</a>}</td></tr>
            ))}
          </tbody>
        </table>
        {invoices.length === 0 && <p className="px-5 py-8 text-sm text-slate-500">No invoices yet.</p>}
      </div>
      <p className="text-xs text-slate-500">Prices in AUD. GST treatment to be confirmed before live payments. We never store full card details. See the <Link to="/legal/cancellation" className="underline">cancellation policy</Link>.</p>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  const tries = useRef(0);
  useEffect(() => {
    const id = params.get("session_id");
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid" || data.payment_status === "no_payment_required") {
          setState("paid");
          await refresh();
          return;
        }
      } catch (e) {
        setState("error");
        return;
      }
      if (tries.current < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-md w-full text-center" data-testid="payment-result">
        {state === "checking" && <><Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" /><p className="mt-4 font-semibold">Confirming your payment…</p></>}
        {state === "paid" && <><CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" /><h1 className="mt-4 text-2xl font-bold" data-testid="payment-success">Payment successful</h1><p className="mt-2 text-slate-600">Your subscription is active and full access has been restored.</p><button onClick={() => nav("/app")} className="mt-6 bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-md" data-testid="payment-continue-btn">Go to dashboard</button></>}
        {(state === "pending" || state === "error") && <><h1 className="text-xl font-bold">Payment not confirmed yet</h1><p className="mt-2 text-slate-600">We haven't received confirmation from Stripe. Check the billing page in a minute.</p><Link to="/app/billing" className="mt-6 inline-block text-sky-700 font-semibold">Go to billing</Link></>}
      </div>
    </div>
  );
}
